import * as React from 'react';
import styled from '@emotion/styled';
import { __ } from '../../../helpers/TranslationService';
import { IBreadcrumb, ICruiseIconOption } from '../../../interfaces';
import * as InclusivesIcons from '../helper/InclusiveIcons';
import { formatItineraryInfo, formatShipTitleArticle } from '../../../helpers/cruiseSearchHelper';
import parse from 'html-react-parser';

export interface ICruiseStageHeader {
    nid: number;
    title: string;
    stars: number;
    companyLogo?: string;
    companyTitle?: string;
    packageOperatorLogo?: string;
    packageOperatorTitle?: string;
    breadcrumbs?: IBreadcrumb[];
    icons?: {
        icon: ICruiseIconOption;
        text: string;
    }[];
    nights?: number;
    waypoints?: any;
    isDreamlinesPackage?: boolean;
}

const SubTitleLink = styled.a`
    display: inline-block;
    color: ${({ theme }) => theme.link.primary};
    text-decoration: none;
    margin-left: 5px;
    &:hover {
        color: ${({ theme }) => theme.link.primaryHover};
    }
`;

const CruiseStageHeader = (props: ICruiseStageHeader) => {
    const {
        nid: cruiseId,
        title,
        stars,
        packageOperatorLogo,
        packageOperatorTitle,
        companyLogo,
        companyTitle,
        breadcrumbs,
        icons,
        nights,
        waypoints,
        isDreamlinesPackage,
    } = props;

    const shipBreadcrumb = breadcrumbs?.length > 1 ? breadcrumbs[breadcrumbs.length - 2] : null;
    const companyBreadcrumb = breadcrumbs?.length > 2 ? breadcrumbs[breadcrumbs.length - 3] : null;
    const waypointsHarboursArr = waypoints?.length
        ? waypoints.filter((waypoint) => !!waypoint.city).map((item) => item.city)
        : [];

    const compName = 'cruise-stage-header';
    const uniqueWayPointCities = Array.from(new Set(waypointsHarboursArr));
    const nightsPortsDaysAtSea = formatItineraryInfo(nights, uniqueWayPointCities.length) + ' ';

    return (
        <header className={`${compName}`}>
            <div className={`${compName}__info`}>
                <h1 className={`${compName}__title`}>{title}</h1>
                <div className={`${compName}__iternary-info`}>
                    <span className={`${compName}__iternary-info__night-ports`}>
                        {nightsPortsDaysAtSea}
                    </span>

                    {shipBreadcrumb && (
                        <span>
                            {formatShipTitleArticle(companyTitle)}
                            <SubTitleLink href={shipBreadcrumb.url} data-testid="ship-url">
                                {parse(shipBreadcrumb.text)}
                            </SubTitleLink>
                        </span>
                    )}
                    <span className={`${compName}__iternary-info__company`}>{companyTitle}</span>
                    <span className={`${compName}__iternary-info__cruise-id`}>#{cruiseId}</span>
                </div>

                {icons.length > 0 && (
                    <>
                        <div className={`${compName}__inclusives-row`}>
                            <span className={`${compName}__inclusives__heading`}>
                                {__('inclusiveHeadline', 'dreamlines')}
                            </span>
                            <ul className={`${compName}__inclusives`}>
                                {icons.map((icon, i) => {
                                    const IconComp = InclusivesIcons[icon?.icon];
                                    if (!IconComp) {
                                        return null;
                                    }
                                    return (
                                        <li
                                            key={`inclusive${i}`}
                                            className={`${compName}__inclusives__item`}
                                        >
                                            <span className={`${compName}__inclusives__item__icon`}>
                                                <IconComp width={12} height={12} />
                                            </span>
                                            <span
                                                className={`${compName}__inclusives__item__title`}
                                            >
                                                {__(icon.text, 'dreamlines')}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </>
                )}
            </div>
            {!!packageOperatorLogo && companyLogo !== packageOperatorLogo && companyBreadcrumb && (
                <a
                    href={companyBreadcrumb.url}
                    className={`${compName}__logo`}
                    title={packageOperatorTitle}
                >
                    <div className={`${compName}__logo__img-holder`}>
                        <img
                            className={`${compName}__logo__img-holder__img`}
                            src={packageOperatorLogo}
                            alt={packageOperatorTitle}
                        />
                    </div>
                </a>
            )}
            {!!companyLogo && companyBreadcrumb !== null && (
                <a
                    href={companyBreadcrumb.url}
                    className={`${compName}__logo`}
                    title={companyTitle}
                >
                    <div className={`${compName}__logo__img-holder`}>
                        <img
                            className={`${compName}__logo__img-holder__img`}
                            src={companyLogo}
                            alt={companyTitle}
                        />
                    </div>
                </a>
            )}
        </header>
    );
};

export default CruiseStageHeader;
